import { useEffect, useState, useRef } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { api } from "@/lib/api";
import {
  Gauge,
  ShoppingBag,
  TShirt,
  Factory,
  Package,
  Truck,
  Warehouse,
  UsersThree,
  ChartLineUp,
  PlugsConnected,
  Gear,
  MagnifyingGlass,
  SignOut,
  Bell,
  Translate,
  Barcode,
  Coins,
} from "@phosphor-icons/react";

const NAV = [
  { key: "nav.commandCenter", to: "/", icon: Gauge, testId: "nav-command-center" },
  { key: "nav.salesDashboard", to: "/sales/dashboard", icon: ShoppingBag, testId: "nav-sales-dash" },
  { key: "nav.salesNew", to: "/sales/new", icon: ShoppingBag, testId: "nav-sales-new", indent: true },
  { key: "nav.salesAll", to: "/sales", icon: ShoppingBag, testId: "nav-sales-all", indent: true },
  { key: "nav.salesShowroom", to: "/sales/showroom", icon: ShoppingBag, testId: "nav-sales-showroom", indent: true },
  { key: "nav.salesReturns", to: "/sales/returns", icon: ShoppingBag, testId: "nav-sales-returns", indent: true },
  { key: "nav.ordersDashboard", to: "/orders/dashboard", icon: ShoppingBag, testId: "nav-orders-dash" },
  { key: "nav.ordersAll", to: "/orders", icon: ShoppingBag, testId: "nav-orders-all", indent: true },
  { key: "nav.ordersShowroom", to: "/orders/showroom", icon: ShoppingBag, testId: "nav-orders-showroom", indent: true },
  { key: "nav.ordersMTO", to: "/orders/mto", icon: ShoppingBag, testId: "nav-orders-mto", indent: true },
  { key: "nav.reservations", to: "/orders/reservations", icon: ShoppingBag, testId: "nav-reservations", indent: true },
  { key: "nav.productionDashboard", to: "/production/dashboard", icon: Factory, testId: "nav-prod-dash" },
  { key: "nav.productionPlanning", to: "/production/planning", icon: Factory, testId: "nav-prod-planning", indent: true },
  { key: "nav.productionOrders", to: "/production/orders", icon: Factory, testId: "nav-prod-orders", indent: true },
  { key: "nav.wipBoard", to: "/production/wip", icon: Factory, testId: "nav-wip", indent: true },
  { key: "nav.materialRequirements", to: "/production/materials", icon: Factory, testId: "nav-matreq", indent: true },
  { key: "nav.qc", to: "/production/qc", icon: Factory, testId: "nav-qc", indent: true },
  { key: "nav.products", to: "/products", icon: TShirt, testId: "nav-products" },
  { key: "nav.collections", to: "/collections", icon: TShirt, testId: "nav-collections", indent: true },
  { key: "nav.bom", to: "/bom", icon: TShirt, testId: "nav-bom", indent: true },
  { key: "nav.barcodes", to: "/barcodes", icon: Barcode, testId: "nav-barcodes", indent: true },
  { key: "nav.materials", to: "/materials", icon: Package, testId: "nav-materials" },
  { key: "nav.fabricLibrary", to: "/fabric-library", icon: Package, testId: "nav-fabric-library", indent: true },
  { key: "nav.purchasing", to: "/suppliers", icon: Truck, testId: "nav-purchasing" },
  { key: "nav.inventory", to: "/inventory", icon: Warehouse, testId: "nav-inventory" },
  { key: "nav.customers", to: "/customers", icon: UsersThree, testId: "nav-customers" },
  { key: "nav.reports", to: "/reports", icon: ChartLineUp, testId: "nav-reports", soon: true },
  { key: "nav.finance", to: "/finance", icon: Coins, testId: "nav-finance" },
  { key: "nav.expenses", to: "/finance/expenses", icon: Coins, testId: "nav-expenses", indent: true },
  { key: "nav.payables", to: "/finance/payables", icon: Coins, testId: "nav-payables", indent: true },
  { key: "nav.integrations", to: "/integrations", icon: PlugsConnected, testId: "nav-integrations" },
  { key: "nav.admin", to: "/admin", icon: Gear, testId: "nav-admin" },
];

function LanguageSwitcher() {
  const { lang, setLang, langs, t } = useI18n();
  return (
    <div className="flex items-center gap-1" data-testid="lang-switcher">
      <Translate size={14} weight="light" className="text-[#8F8D84]" />
      {langs.map((l) => (
        <button
          key={l.code}
          onClick={() => setLang(l.code)}
          data-testid={`lang-btn-${l.code}`}
          className="px-2 py-1 text-[10px] uppercase tracking-[0.2em] transition-colors"
          style={{
            color: lang === l.code ? "#1A1A1A" : "#8F8D84",
            borderBottom: lang === l.code ? "1px solid var(--nylsu-gold)" : "1px solid transparent",
          }}
          aria-label={t("common.language") + ": " + l.label}
        >
          {l.short}
        </button>
      ))}
    </div>
  );
}

function GlobalSearch() {
  const { t } = useI18n();
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const nav = useNavigate();

  useEffect(() => {
    if (q.trim().length < 2) { setRows([]); return; }
    const id = setTimeout(async () => {
      try {
        const r = await api.get(`/search?q=${encodeURIComponent(q.trim())}`);
        setRows(r.data);
        setOpen(true);
      } catch { setRows([]); }
    }, 250);
    return () => clearTimeout(id);
  }, [q]);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const pathFor = (r) => {
    if (r.type === "order") return `/orders/${r.id}`;
    if (r.type === "production_order") return `/production/orders/${r.id}`;
    if (r.type === "customer") return `/customers`;
    if (r.type === "style") return `/products`;
    if (r.type === "material") return `/materials`;
    return "/";
  };

  return (
    <div ref={ref} className="flex-1 max-w-xl relative">
      <div
        className="flex items-center gap-3 px-3 py-2 border rounded-none"
        style={{ borderColor: "var(--nylsu-border)", background: "var(--nylsu-card)" }}
      >
        <MagnifyingGlass size={16} weight="light" className="text-[#8F8D84]" />
        <input
          data-testid="global-search-input"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onFocus={() => q && setOpen(true)}
          placeholder={t("common.searchPlaceholder")}
          className="flex-1 bg-transparent outline-none text-sm placeholder:text-[#8F8D84]"
        />
        <span className="overline hidden sm:inline">⌘K</span>
      </div>
      {open && rows.length > 0 && (
        <div className="absolute z-40 top-full left-0 right-0 border mt-1 max-h-96 overflow-y-auto"
          style={{ background: "var(--nylsu-card)", borderColor: "var(--nylsu-border)" }}
          data-testid="global-search-results">
          {rows.map((r) => (
            <button
              key={`${r.type}-${r.id}`}
              onClick={() => { nav(pathFor(r)); setOpen(false); setQ(""); }}
              data-testid={`search-result-${r.id}`}
              className="w-full text-left px-4 py-3 border-b hover:bg-[#F4F3ED]"
              style={{ borderColor: "var(--nylsu-border)" }}
            >
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#8F8D84]">{r.type.replace("_", " ")}</div>
              <div className="font-serif text-sm">{r.label}</div>
              {r.detail && <div className="text-xs text-[#5C5A54]">{r.detail}</div>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function NotificationBell() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const load = async () => {
    try { setRows((await api.get("/notifications?limit=15")).data); }
    catch { setRows([]); }
  };
  useEffect(() => {
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, []);
  const unread = rows.filter((r) => !r.read_by || r.read_by.length === 0);
  return (
    <div className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className="p-2 relative text-[#5C5A54] hover:text-[#1A1A1A]"
        data-testid="topbar-notifications-btn"
        aria-label="Notifications"
      >
        <Bell size={18} weight="light" />
        {unread.length > 0 && (
          <span className="absolute -top-0.5 -right-0.5 text-[9px] font-mono px-1 py-0.5"
            style={{ background: "var(--nylsu-danger)", color: "white" }}
            data-testid="notif-badge">
            {unread.length > 9 ? "9+" : unread.length}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-2 w-96 border z-40 max-h-96 overflow-y-auto"
          style={{ background: "var(--nylsu-card)", borderColor: "var(--nylsu-border)" }}
          data-testid="notif-dropdown">
          {rows.length === 0 && <div className="p-4 text-xs text-[#8F8D84]">No notifications</div>}
          {rows.map((n) => (
            <div key={n.id} className="p-4 border-b" style={{ borderColor: "var(--nylsu-border)" }}
              data-testid={`notif-${n.id}`}>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#8F8D84]">{n.kind.replace(/_/g, " ")}</div>
              <div className="font-serif text-sm mt-1">{n.title}</div>
              {n.message && <div className="text-xs text-[#5C5A54] mt-1">{n.message}</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();
  return (
    <div className="nylsu-noise flex min-h-screen" data-testid="app-shell">
      <aside
        className="hidden md:flex flex-col w-[248px] shrink-0 border-r"
        style={{ background: "var(--nylsu-sidebar)", borderColor: "var(--nylsu-border)" }}
        data-testid="sidebar"
      >
        <div className="px-6 py-8 border-b" style={{ borderColor: "var(--nylsu-border)" }}>
          <div className="font-serif text-2xl tracking-[0.3em] leading-none">{t("common.appName")}</div>
          <div className="overline mt-2">{t("common.appTagline")}</div>
        </div>

        <nav className="flex-1 py-4 overflow-y-auto">
          {NAV.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                data-testid={item.testId}
                className={({ isActive }) =>
                  `flex items-center gap-3 py-3 text-sm transition-[background,color,border-color] border-l-2 ${item.indent ? "pl-11 pr-6" : "px-6"} ${
                    isActive ? "text-[#1A1A1A] font-medium" : "text-[#5C5A54] hover:text-[#1A1A1A] border-transparent"
                  }`
                }
                style={({ isActive }) => ({
                  borderLeftColor: isActive ? "var(--nylsu-gold)" : "transparent",
                  background: isActive ? "rgba(194,168,120,0.08)" : "transparent",
                })}
              >
                {!item.indent && <Icon size={18} weight="light" />}
                <span className="flex-1">{t(item.key)}</span>
                {item.soon && (
                  <span className="text-[9px] uppercase tracking-[0.15em] text-[#8F8D84]">{t("nav.soon")}</span>
                )}
              </NavLink>
            );
          })}
        </nav>

        <div className="px-6 py-4 border-t" style={{ borderColor: "var(--nylsu-border)" }}>
          <LanguageSwitcher />
        </div>

        <div className="px-6 py-5 border-t" style={{ borderColor: "var(--nylsu-border)" }}>
          <div className="flex items-center gap-3">
            <div
              className="w-9 h-9 rounded-full flex items-center justify-center font-serif text-lg"
              style={{ background: "#1A1A1A", color: "var(--nylsu-page)" }}
            >
              {user?.name?.[0] || "N"}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm truncate" data-testid="sidebar-user-name">
                {user?.name}
              </div>
              <div className="overline truncate">{t(`role.${user?.role}`, user?.role_label || "")}</div>
            </div>
            <button
              data-testid="sidebar-logout-btn"
              onClick={async () => {
                await logout();
                nav("/login");
              }}
              className="p-2 hover:text-[#8C3B3B] transition-colors"
              aria-label={t("auth.logout")}
            >
              <SignOut size={18} weight="light" />
            </button>
          </div>
        </div>
      </aside>

      <main className="flex-1 min-w-0 flex flex-col">
        <header
          className="h-16 border-b flex items-center px-6 md:px-10 gap-4"
          style={{ background: "var(--nylsu-page)", borderColor: "var(--nylsu-border)" }}
          data-testid="topbar"
        >
          <GlobalSearch />
          <NotificationBell />
        </header>

        <div className="flex-1 px-6 md:px-10 py-8 md:py-10">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
